import React from "react";
import PropTypes from "prop-types";
import {
  Checkbox,
  FormControlLabel,
  IconButton,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import BulkCategorizationPanel from "./BulkCategorizationPanel";
import HardDeletePanel from "./HardDeletePanel";
import CatalogToolbarSelect from "./CatalogToolbarSelect";
import { CELL_MATCH_MODE_OPTIONS } from "./utils/cellMatchModeUtils";
import { CM_TOOLTIPS } from "./utils/tooltipsContent";
import {
  CATALOG_DB_COLUMN_LABELS,
  PRODUCT_COLUMN_LABELS,
} from "./utils/catalogFieldLabels";

const TRI_STATE_OPTIONS = [
  { value: "", label: "Tutti" },
  { value: "0", label: "0 / No" },
  { value: "1", label: "1 / Sì" },
];

const CERTIFICATO_OPTIONS = [
  { value: "", label: "Tutti" },
  { value: "true", label: "Certificato" },
  { value: "false", label: "Non certificato" },
];

const STATO_OPTIONS = [
  { value: "", label: "Tutti" },
  { value: "1", label: "1 — Da categorizzare" },
  { value: "2", label: "2 — In lavorazione" },
  { value: "3", label: "3 — Categorizzato" },
];

const PRODUCT_CODE_FILTER_OPTIONS = [
  { value: "", label: "Tutti" },
  { value: "int_mismatch", label: "Mismatch INT vs codice_interno" },
  { value: "ean_mismatch", label: "Mismatch EAN vs codice_ean" },
  { value: "non_standard", label: "Codice prodotto non INT/EAN" },
];

const CatalogOutputToolbar = ({
  filters,
  onFilterChange,
  productColumns,
  catalogColumns,
  visibleProductColumns,
  visibleCatalogColumns,
  onToggleProductColumn,
  onToggleCatalogColumn,
  onResetColumns,
  bulkCategorizeRules,
  onBulkCategorizeRulesChange,
  onBulkCategorizeExecuteAll,
  executingBulkCategorize = false,
  bulkCategorizeDisabled = false,
  bulkShowMatchesOnly = false,
  onBulkShowMatchesOnlyChange,
  hardDeleteRules,
  onHardDeleteRulesChange,
  onHardDeleteExecuteAll,
  executingHardDelete = false,
  hardDeleteDisabled = false,
  hardShowMatchesOnly = false,
  onHardShowMatchesOnlyChange,
}) => {
  const columnFilterOptions = [
    { value: "", label: "Seleziona colonna" },
    ...productColumns.map((column) => ({
      value: column,
      label: `products.${PRODUCT_COLUMN_LABELS[column] || column}`,
    })),
    ...catalogColumns.map((column) => ({
      value: column,
      label: `catalogs.${CATALOG_DB_COLUMN_LABELS[column] || column}`,
    })),
  ];

  const columnFilters = filters.columnFilters?.length
    ? filters.columnFilters
    : [{ id: "cf-default", columnName: "", columnMode: "eq", columnValue: "" }];

  const renderFilterSelect = (field, label, options, placeholder = "Tutti") => (
    <div className="cm-catalog-filter-control">
      <span className="cm-catalog-filter-label">{label}</span>
      <CatalogToolbarSelect
        className="cm-catalog-filter-select"
        value={filters[field] ?? ""}
        onChange={(value) => onFilterChange(field, value)}
        options={options}
        placeholder={placeholder}
        ariaLabel={label}
      />
    </div>
  );

  const updateColumnFilter = (filterId, filterField, filterValue) => {
    onFilterChange("columnFilterUpdate", { id: filterId, filterField, filterValue });
  };

  return (
    <div className="cm-tab-toolbar cm-catalog-toolbar">
      <div className="cm-catalog-filters-panel">
        <section className="cm-catalog-filters-section">
          <Typography variant="subtitle2" className="cm-catalog-panel-heading">
            Filtri catalogo
          </Typography>
          <div className="cm-catalog-filters-grid">
            <FormControlLabel
              className="cm-catalog-filter-checkbox"
              control={(
                <Checkbox
                  size="small"
                  checked={Boolean(filters.uncategorizedOnly)}
                  onChange={(event) => onFilterChange("uncategorizedOnly", event.target.checked)}
                />
              )}
              label="Solo categoria 456 (Da categorizzare)"
            />
            {renderFilterSelect("isDisattivato", "isDisattivato", TRI_STATE_OPTIONS)}
            {renderFilterSelect("isDeleted", "is_deleted", TRI_STATE_OPTIONS)}
            {renderFilterSelect("certificato", "certificato", CERTIFICATO_OPTIONS)}
            {renderFilterSelect("stato", "stato categorizzazione", STATO_OPTIONS)}
            {renderFilterSelect(
              "productCodeFilter",
              "Codice prodotto",
              PRODUCT_CODE_FILTER_OPTIONS,
            )}
          </div>

          <div className="cm-catalog-column-filters-block">
            <Typography variant="caption" className="cm-catalog-column-filters-heading">
              Filtri colonna
            </Typography>
            <div className="cm-catalog-column-filters-list">
              {columnFilters.map((columnFilter, index) => (
                <div key={columnFilter.id} className="cm-catalog-column-match-filter">
                  <div className="cm-catalog-filter-control">
                    {index === 0 && <span className="cm-catalog-filter-label">Colonna</span>}
                    <CatalogToolbarSelect
                      className="cm-catalog-filter-select"
                      value={columnFilter.columnName ?? ""}
                      onChange={(value) => updateColumnFilter(columnFilter.id, "columnName", value)}
                      options={columnFilterOptions}
                      placeholder="Seleziona colonna"
                      ariaLabel="Colonna filtro catalogo"
                    />
                  </div>
                  <div className="cm-catalog-filter-control cm-catalog-filter-mode">
                    {index === 0 && <span className="cm-catalog-filter-label">Mode</span>}
                    <CatalogToolbarSelect
                      className="cm-cell-match-mode-select"
                      value={columnFilter.columnMode ?? "eq"}
                      onChange={(value) => updateColumnFilter(columnFilter.id, "columnMode", value)}
                      options={CELL_MATCH_MODE_OPTIONS}
                      placeholder="Mode"
                      ariaLabel="Modalita filtro colonna catalogo"
                      disabled={!columnFilter.columnName}
                    />
                  </div>
                  <div className="cm-catalog-filter-control">
                    {index === 0 && <span className="cm-catalog-filter-label">Valore</span>}
                    <input
                      type="text"
                      className="cm-input cm-catalog-filter-input"
                      placeholder="Valore (vuoto = null/empty)"
                      value={columnFilter.columnValue ?? ""}
                      disabled={!columnFilter.columnName}
                      onChange={(event) => updateColumnFilter(
                        columnFilter.id,
                        "columnValue",
                        event.target.value,
                      )}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          updateColumnFilter(
                            columnFilter.id,
                            "columnValue",
                            event.currentTarget.value,
                          );
                        }
                      }}
                    />
                  </div>
                  <div className="cm-catalog-column-filter-actions">
                    {index === 0 && <span className="cm-catalog-filter-label cm-catalog-filter-label-spacer" aria-hidden="true">&nbsp;</span>}
                    <div className="cm-catalog-column-filter-buttons">
                      {columnFilters.length > 1 && (
                        <IconButton
                          size="small"
                          aria-label="Rimuovi filtro colonna"
                          title="Rimuovi filtro colonna"
                          onClick={() => onFilterChange("columnFilterRemove", columnFilter.id)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )}
                      {index === columnFilters.length - 1 && (
                        <IconButton
                          size="small"
                          aria-label="Aggiungi filtro colonna"
                          title="Aggiungi filtro colonna"
                          onClick={() => onFilterChange("columnFilterAdd")}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button
            type="button"
            className="cm-btn cm-btn-secondary cm-catalog-panel-reset-btn"
            onClick={() => onFilterChange("reset", true)}
          >
            Reset filtri
          </button>
        </section>

        <section className="cm-catalog-filters-section">
          <Typography variant="subtitle2" className="cm-catalog-panel-heading">
            Colonne visibili
          </Typography>
          <div className="cm-catalog-columns-group">
            <Typography variant="caption" className="cm-catalog-columns-heading">
              products
            </Typography>
            <div className="cm-catalog-columns-list">
              {productColumns.map((column) => (
                <FormControlLabel
                  key={`vis-p-${column}`}
                  className="cm-catalog-column-option"
                  control={(
                    <Checkbox
                      size="small"
                      checked={visibleProductColumns.includes(column)}
                      onChange={() => onToggleProductColumn(column)}
                    />
                  )}
                  label={PRODUCT_COLUMN_LABELS[column] || column}
                />
              ))}
            </div>
          </div>
          <div className="cm-catalog-columns-group">
            <Typography variant="caption" className="cm-catalog-columns-heading">
              catalogs
            </Typography>
            <div className="cm-catalog-columns-list">
              {catalogColumns.map((column) => (
                <FormControlLabel
                  key={`vis-c-${column}`}
                  className="cm-catalog-column-option"
                  control={(
                    <Checkbox
                      size="small"
                      checked={visibleCatalogColumns.includes(column)}
                      onChange={() => onToggleCatalogColumn(column)}
                    />
                  )}
                  label={CATALOG_DB_COLUMN_LABELS[column] || column}
                />
              ))}
            </div>
          </div>
          <button
            type="button"
            className="cm-btn cm-btn-secondary"
            onClick={onResetColumns}
          >
            Seleziona tutto
          </button>
        </section>
      </div>

      <div className="cm-catalog-toolbar-main-row">
        {onBulkCategorizeRulesChange && onBulkCategorizeExecuteAll && (
          <BulkCategorizationPanel
            rules={bulkCategorizeRules || []}
            onRulesChange={onBulkCategorizeRulesChange}
            onExecuteAll={onBulkCategorizeExecuteAll}
            executing={executingBulkCategorize}
            disabled={bulkCategorizeDisabled}
            showMatchesOnly={bulkShowMatchesOnly}
            onShowMatchesOnlyChange={onBulkShowMatchesOnlyChange}
          />
        )}
        {onHardDeleteRulesChange && onHardDeleteExecuteAll && (
          <HardDeletePanel
            productColumns={productColumns}
            catalogColumns={catalogColumns}
            rules={hardDeleteRules || []}
            onRulesChange={onHardDeleteRulesChange}
            onExecuteAll={onHardDeleteExecuteAll}
            executing={executingHardDelete}
            disabled={hardDeleteDisabled}
            showMatchesOnly={hardShowMatchesOnly}
            onShowMatchesOnlyChange={onHardShowMatchesOnlyChange}
          />
        )}
      </div>

    </div>
  );
};

CatalogOutputToolbar.propTypes = {
  filters: PropTypes.shape({
    uncategorizedOnly: PropTypes.bool,
    isDisattivato: PropTypes.string,
    isDeleted: PropTypes.string,
    certificato: PropTypes.string,
    stato: PropTypes.string,
    productCodeFilter: PropTypes.string,
    columnFilters: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      columnName: PropTypes.string,
      columnMode: PropTypes.string,
      columnValue: PropTypes.string,
    })),
  }).isRequired,
  onFilterChange: PropTypes.func.isRequired,
  productColumns: PropTypes.arrayOf(PropTypes.string).isRequired,
  catalogColumns: PropTypes.arrayOf(PropTypes.string).isRequired,
  visibleProductColumns: PropTypes.arrayOf(PropTypes.string).isRequired,
  visibleCatalogColumns: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggleProductColumn: PropTypes.func.isRequired,
  onToggleCatalogColumn: PropTypes.func.isRequired,
  onResetColumns: PropTypes.func.isRequired,
  bulkCategorizeRules: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    alberoValue: PropTypes.string,
    categoryId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    categoryLabel: PropTypes.string,
    colorIndex: PropTypes.number,
  })),
  onBulkCategorizeRulesChange: PropTypes.func,
  onBulkCategorizeExecuteAll: PropTypes.func,
  executingBulkCategorize: PropTypes.bool,
  bulkCategorizeDisabled: PropTypes.bool,
  bulkShowMatchesOnly: PropTypes.bool,
  onBulkShowMatchesOnlyChange: PropTypes.func,
  hardDeleteRules: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    table: PropTypes.string,
    column: PropTypes.string,
    value: PropTypes.string,
    mode: PropTypes.string,
  })),
  onHardDeleteRulesChange: PropTypes.func,
  onHardDeleteExecuteAll: PropTypes.func,
  executingHardDelete: PropTypes.bool,
  hardDeleteDisabled: PropTypes.bool,
  hardShowMatchesOnly: PropTypes.bool,
  onHardShowMatchesOnlyChange: PropTypes.func,
};

export default CatalogOutputToolbar;
